import { useEffect, useMemo } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useLastLoginStore } from "@/store/lastLoginStore";

/** Fallback window when we have no recorded prior login for THIS user (e.g.
 *  first visit on a new device): show the last 48 hours of station activity. */
const FALLBACK_LOOKBACK_MS = 48 * 60 * 60 * 1000;

// The authkit User type carries lastSignInAt, but read it defensively so a
// version skew in the SDK types can't break the build.
type UserWithLastSignIn = { id?: string; lastSignInAt?: string | null };

export interface LastLoginWindow {
  /** ISO start of the activity window (previous login, or the 48h fallback). */
  windowStart: string;
  /** ISO end of the window (now). Stable per hook instance. */
  windowEnd: string;
  /** The admin's previous session sign-in, or null if none is known yet. */
  previousSignInAt: string | null;
  /** False when we fell back to the 48h lookback (no prior login recorded). */
  hasPriorLogin: boolean;
}

/**
 * Resolves the "since you last logged in" window for the Station Admin
 * dashboard. Records the admin's WorkOS sign-in into the persisted
 * lastLoginStore and returns the previous session boundary as the window
 * start, falling back to a 48h lookback the first time we see this admin.
 */
export function useLastLoginWindow(): LastLoginWindow {
  const { user } = useAuth();
  const authUser = user as UserWithLastSignIn | null;
  const userId = authUser?.id ?? null;
  const lastSignInAt = authUser?.lastSignInAt ?? null;

  const storedUserId = useLastLoginStore((s) => s.userId);
  const storedPrevious = useLastLoginStore((s) => s.previousSignInAt);
  const recordSignIn = useLastLoginStore((s) => s.recordSignIn);

  useEffect(() => {
    recordSignIn(userId, lastSignInAt);
  }, [userId, lastSignInAt, recordSignIn]);

  // Only honor the stored boundary when it belongs to the signed-in user —
  // before the recording effect fires, the store may still hold the previous
  // browser user's record, which must never bound this user's window.
  const previousSignInAt =
    userId !== null && storedUserId === userId ? storedPrevious : null;

  // Freeze the window boundaries per (previousSignInAt) so downstream query
  // keys stay stable — otherwise the "now" end and the 48h fallback would move
  // every render and thrash the report/evidence queries.
  return useMemo<LastLoginWindow>(() => {
    const hasPriorLogin = previousSignInAt !== null;
    return {
      windowStart: hasPriorLogin
        ? (previousSignInAt as string)
        : new Date(Date.now() - FALLBACK_LOOKBACK_MS).toISOString(),
      windowEnd: new Date().toISOString(),
      previousSignInAt,
      hasPriorLogin,
    };
  }, [previousSignInAt]);
}
